import { useCallback, useState } from "react";

const useRouteHandler = () => {
    const randomId = (length = 6) => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

        return Array.from({ length }, () =>
            chars[Math.floor(Math.random() * chars.length)]
        ).join("");
    };

    const [routeDraft, setRouteDraft] = useState({
        name: "",
        description: "",
        routing: {
            costing: "bus",
            costingOptions: {},
        },
        waypoints: [],
        route: null,
    });

    const [waypointDraft, setWaypointDraft] = useState({
        name: "",
        address: "",
        waitingSeconds: 0,
        location: null,
    });

    const [selectingPoint, setSelectingPoint] = useState(false);

    const setRouteName = useCallback((name) => {
        setRouteDraft((prev) => ({
            ...prev,
            name
        }));
    }, []);

    const setRouteDescription = useCallback((description) => {
        setRouteDraft((prev) => ({
            ...prev,
            description
        }));
    }, []);

    const setWaypointName = useCallback((name) => {
        setWaypointDraft((prev) => ({
            ...prev,
            name
        }));
    }, []);

    const setWaypointAddress = useCallback((address) => {
        setWaypointDraft((prev) => ({
            ...prev,
            address
        }));
    }, []);

    const setWaypointWaitingSeconds = useCallback((waitingSeconds) => {
        setWaypointDraft((prev) => ({
            ...prev,
            waitingSeconds
        }));
    }, []);

    const startPointSelection = useCallback(() => {
        setSelectingPoint(true);
    }, []);

    const handleMapPoint = useCallback((lng, lat) => {
        if (!selectingPoint) return;

        setWaypointDraft((prev) => ({
            ...prev,
            location: {
                type: "Point",
                coordinates: [lng, lat]
            }
        }));
    }, [selectingPoint]);

    const addWaypoint = useCallback(() => {
        if (!waypointDraft.name.trim()) return false;
        if (!waypointDraft.location) return false;

        const waypoint = {
            waypointId: randomId(),

            name: waypointDraft.name.trim(),
            address: waypointDraft.address.trim(),

            waitingSeconds: waypointDraft.waitingSeconds,

            location: waypointDraft.location
        };

        setRouteDraft((prev) => ({
            ...prev,
            waypoints: [...prev.waypoints, waypoint],
            //existing calculated route is no longer valid.
            route: null
        }));

        setWaypointDraft({
            name: "",
            address: "",
            waitingSeconds: 0,
            location: null,
        });

        setSelectingPoint(false);

        return true;
    }, [waypointDraft]);

    const removeWaypoint = useCallback((waypointId) => {
        setRouteDraft((prev) => ({
            ...prev,
            waypoints: prev.waypoints.filter(
                (waypoint) => waypoint.waypointId !== waypointId
            ),
            route: null
        }));
    }, []);

    //for routing with valhalla
    const normalizeValhallaRoute = (data, waypoints) => {
        const trip = data?.trip;

        if (!trip) {
            throw new Error("Invalid valhalla route response");
        }

        return {
            shapeFormat: "polyline6",
            legs: trip.legs.map((leg, index) => ({
                sequence: index,

                fromWaypointId: waypoints[index].waypointId,
                toWaypointId: waypoints[index + 1].waypointId,

                encodedPolyline: leg.shape,

                distanceMeters: Math.round(leg.summary.length * 1000),
                durationSeconds: Math.round(leg.summary.time)
            })),
            summary: {
                distanceMeters: Math.round(trip.summary.length * 1000),
                durationSeconds: Math.round(trip.summary.time),
            },
        }
    };

    return {
        routeDraft,
        setRouteDraft,
        waypointDraft,
        selectingPoint,

        setRouteName,
        setRouteDescription,
        setWaypointName,
        setWaypointAddress,
        setWaypointWaitingSeconds,

        startPointSelection,
        handleMapPoint,
        addWaypoint,
        removeWaypoint,

        normalizeValhallaRoute
    };
};

export default useRouteHandler;

/**
 * useRouteHandler scopes
├── route draft
├── stop draft
├── point selection
├── add/remove stop
├── reorder stops
├── edit stop
├── normalize sequences/types
├── calculate with Valhalla
├── invalidate calculated route
└── save MetaRoute
 */